import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { getFrontFileUrl } from "../../services/apiFront";
import { formatParcours } from "../../services/aspirantUtils";

export default function AspirantsRegionPage() {
  const [aspirants, setAspirants] = useState([]);
  const [validatedAspirants, setValidatedAspirants] = useState([]);
  const [rejectedAspirants, setRejectedAspirants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingValidated, setLoadingValidated] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedAspirant, setSelectedAspirant] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  useEffect(() => {
    loadAspirants();
    loadValidatedAspirants();
    loadRejectedAspirants();
  }, []);

  const loadAspirants = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/aspirants/pending-validation");
      setAspirants(res.data?.aspirants || []);
    } catch (err) {
      console.error("Erreur chargement aspirants:", err);
      setError(err?.response?.data?.error || "Erreur lors du chargement des élèves");
    } finally {
      setLoading(false);
    }
  };

  const loadValidatedAspirants = async () => {
    setLoadingValidated(true);
    try {
      const res = await api.get("/aspirants/validated");
      setValidatedAspirants(res.data?.aspirants || []);
    } catch (err) {
      console.error("Erreur chargement aspirants validés:", err);
    } finally {
      setLoadingValidated(false);
    }
  };

  const loadRejectedAspirants = async () => {
    setLoadingRejected(true);
    try {
      const res = await api.get("/aspirants/rejected");
      setRejectedAspirants(res.data?.aspirants || []);
    } catch (err) {
      console.error("Erreur chargement aspirants rejetés:", err);
    } finally {
      setLoadingRejected(false);
    }
  };

  const openModal = (aspirant) => {
    setSelectedAspirant(aspirant);
    setComment(aspirant.rejection_comment || "");
    setModalError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAspirant(null);
    setComment("");
    setModalError("");
  };

  const handleValidation = async (action) => {
    if (!selectedAspirant) return;
    
    // Si on refuse, le commentaire est obligatoire
    if (action === "reject" && (!comment || !comment.trim())) {
      setModalError("Le commentaire est obligatoire pour refuser une inscription.");
      return;
    }
    
    // Réinitialiser l'erreur si tout est OK
    setModalError("");
    setSubmitting(true);
    try {
      const response = await api.patch(`/aspirants/${selectedAspirant.aspirant_id}/validate`, {
        action: action,
        comment: action === "reject" ? comment.trim() : null
      });
      
      // Recharger les listes
      await loadAspirants();
      await loadValidatedAspirants();
      await loadRejectedAspirants();
      closeModal();
      
      // Afficher un message de succès
      if (action === "validate") {
        alert("Elève validé avec succès !");
      } else {
        alert("Inscription refusée.");
      }
    } catch (err) {
      console.error("Erreur validation:", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Erreur lors de la validation";
      alert(errorMessage);
      
      // Si le token est invalide, rediriger vers la page de connexion
      if (err?.response?.status === 401 || errorMessage.includes("token")) {
        // Optionnel: rediriger vers login
        // window.location.href = "/login";
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    // Utiliser la fonction utilitaire qui masque l'URL du backend
    return getFrontFileUrl(filePath);
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow border-0 mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-people-fill me-2"></i>
            Validation des inscriptions d'élèves
          </h5>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : aspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle text-success" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucun élève en attente de validation.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Matricule</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Parcours</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>
                          {aspirant.matricule ? (
                            <span className="badge bg-success">{aspirant.matricule}</span>
                          ) : (
                            <span className="badge bg-warning text-dark">En attente</span>
                          )}
                        </td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td className="text-center">
                          {!aspirant.is_validated_by_region ? (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openModal(aspirant)}
                            >
                              <i className="bi bi-eye me-1"></i>
                              Examiner
                            </button>
                          ) : (
                            <span className="badge bg-success">Validé</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {aspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {aspirant.matricule ? (
                              <span className="badge bg-success">{aspirant.matricule}</span>
                            ) : (
                              <span className="badge bg-warning text-dark">En attente</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-3">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>
                      
                      <div className="d-grid">
                        {!aspirant.is_validated_by_region ? (
                          <button
                            className="btn btn-sm btn-primary w-100"
                            onClick={() => openModal(aspirant)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Examiner
                          </button>
                        ) : (
                          <span className="badge bg-success w-100 text-center py-2">Validé</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modale de validation */}
      {showModal && selectedAspirant && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          onClick={closeModal}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-person-check me-2"></i>
                  Validation de l'inscription - {selectedAspirant.name}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeModal}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="text-primary">Informations de l'élève</h6>
                  <p className="mb-1"><strong>Nom :</strong> {selectedAspirant.name}</p>
                  <p className="mb-1"><strong>Parcours :</strong> {formatParcours(selectedAspirant.parcours)}</p>
                  <p className="mb-1"><strong>Église :</strong> {selectedAspirant.eglise_name || "—"}</p>
                  <p className="mb-1"><strong>District :</strong> {selectedAspirant.district_name || "—"}</p>
                </div>

                <div className="mb-3">
                  <h6 className="text-primary">Pièces jointes prérequis</h6>
                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <div className="card">
                        <div className="card-body p-2">
                          <small className="text-muted d-block mb-1">Carte de baptême</small>
                          {selectedAspirant.carte_bapteme ? (
                            <a
                              href={getFileUrl(selectedAspirant.carte_bapteme)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100"
                            >
                              <i className="bi bi-eye me-1"></i>Voir
                            </a>
                          ) : (
                            <span className="text-muted">Non disponible</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="card">
                        <div className="card-body p-2">
                          <small className="text-muted d-block mb-1">Recommandation</small>
                          {selectedAspirant.recommandation ? (
                            <a
                              href={getFileUrl(selectedAspirant.recommandation)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100"
                            >
                              <i className="bi bi-eye me-1"></i>Voir
                            </a>
                          ) : (
                            <span className="text-muted">Non disponible</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="card">
                        <div className="card-body p-2">
                          <small className="text-muted d-block mb-1">
                            {selectedAspirant.parcours === "Senior Youth Leader" ? "Certificat Séminaire de 10h" : "Certificat BST"}
                          </small>
                          {selectedAspirant.certificat ? (
                            <a
                              href={getFileUrl(selectedAspirant.certificat)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100"
                            >
                              <i className="bi bi-eye me-1"></i>Voir
                            </a>
                          ) : (
                            <span className="text-muted">Non disponible</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Commentaire</strong> <small className="text-muted">(optionnel, requis en cas de refus)</small>
                  </label>
                  <textarea
                    className={`form-control ${modalError && !comment.trim() ? "is-invalid" : ""}`}
                    rows="4"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      // Effacer l'erreur quand l'utilisateur commence à taper
                      if (modalError) setModalError("");
                    }}
                    placeholder="Ajoutez un commentaire si nécessaire..."
                  />
                  {modalError && (
                    <div className="invalid-feedback d-block">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {modalError}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer d-flex flex-column flex-md-row gap-2">
                <button
                  type="button"
                  className="btn btn-secondary w-100 w-md-auto"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-danger w-100 w-md-auto"
                  onClick={() => handleValidation("reject")}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-1"></i>
                      Refuser
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-success w-100 w-md-auto"
                  onClick={() => handleValidation("validate")}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-1"></i>
                      Valider
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tableau des élèves validés */}
      <div className="card shadow border-0">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">
            <i className="bi bi-check-circle-fill me-2"></i>
            Élèves validés
          </h5>
        </div>
        <div className="card-body">
          {loadingValidated ? (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : validatedAspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucun élève validé pour le moment.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Matricule</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Parcours</th>
                      <th>Date de validation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validatedAspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>
                          {aspirant.matricule ? (
                            <span className="badge bg-success">{aspirant.matricule}</span>
                          ) : (
                            <span className="badge bg-secondary">—</span>
                          )}
                        </td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td>
                          {aspirant.validated_at ? (
                            new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {validatedAspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {aspirant.matricule ? (
                              <span className="badge bg-success">{aspirant.matricule}</span>
                            ) : (
                              <span className="badge bg-secondary">—</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>
                      
                      {aspirant.validated_at && (
                        <div>
                          <small className="text-muted d-block">Date de validation:</small>
                          <small>
                            {new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tableau des élèves rejetés */}
      <div className="card shadow border-0 mt-4">
        <div className="card-header bg-danger text-white">
          <h5 className="mb-0">
            <i className="bi bi-x-circle-fill me-2"></i>
            Élèves rejetés
          </h5>
        </div>
        <div className="card-body">
          {loadingRejected ? (
            <div className="text-center py-4">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : rejectedAspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle text-success" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucun élève rejeté pour le moment.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Parcours</th>
                      <th>Commentaire de rejet</th>
                      <th>Date de rejet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedAspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td>
                          <span className="text-danger fst-italic">
                            {aspirant.rejection_comment || "—"}
                          </span>
                        </td>
                        <td>
                          {aspirant.validated_at ? (
                            new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {rejectedAspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm border-danger">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>

                      {aspirant.rejection_comment && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Commentaire de rejet:</small>
                          <span className="text-danger fst-italic">{aspirant.rejection_comment}</span>
                        </div>
                      )}
                      
                      {aspirant.validated_at && (
                        <div>
                          <small className="text-muted d-block">Date de rejet:</small>
                          <small>
                            {new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
